import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { BundledOfferState, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useReduxStateListener from 'hooks/useReduxStateListener'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { accountAccessShowLogin } from 'actions/UiActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { generateBundleAndSaveCheckoutItems } from 'lib/checkout/accommodation/cart'
import GeoContext from 'contexts/geoContext'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { addToCartGTM } from 'analytics/eventDefinitions'
import React, { ComponentProps, useCallback, useContext, useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  BundleOfferPackageOptionView,
} from 'selectors/offerPage/bundledOfferPageSelectors'
import TextButton from 'LuxKit/Button/TextButton'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { arrayToObject } from 'lib/array/arrayUtils'
import { addToCart } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import moment from 'moment'
import { allocateTravellersToRooms } from 'businessTraveller/utils/allocateTravellersToRooms'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind'> {
  bundledOffersState: Record<string, BundledOfferState>;
  isLoggedIn: boolean;
  offer: App.BundleOffer;
  packageView: BundleOfferPackageOptionView;
  rooms: Array<App.RoomOccupants>;
  onSubmit?: () => void;
}

function BundleAndSaveBookButton(props: Props) {
  const {
    rooms,
    isLoggedIn,
    packageView,
    offer,
    onSubmit,
    bundledOffersState,
    ...rest
  } = props

  const bundledStates = useMemo(() => Object.values(bundledOffersState ?? {}).filter(Boolean), [bundledOffersState])
  const { currentCurrency } = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const selectedTravellerEmployeeIds = useAppSelector(state => state.businessTraveller.selectedTravellerEmployeeIds)
  const selectedTravellerGuestIds = useAppSelector(state => state.businessTraveller.selectedTravellerGuestIds)

  const loginListener = useReduxStateListener<{ loggedIn: boolean }>((state) => {
    if (isLoggedInSelector(state)) {
      return { loggedIn: true }
    } else if (!state.ui.modal.open) {
      return { loggedIn: false }
    }
  })

  const onBookNow = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.closeFilter })
    fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, packageView?.hotelPrice, sumUpOccupancies(rooms), 'hotel'))

    const dates = arrayToObject(bundledStates,
      state => state.offer!.id,
      (state) => {
        const checkOut = state.checkOutDate ?? moment(state.checkInDate).add(state.duration, 'days').format(ISO_DATE_FORMAT)
        return {
          checkIn: state.checkInDate!,
          checkOut,
          duration: state.duration!,
        }
      })

    // Allocate travellers to rooms
    const roomsWithTravellerInfo = allocateTravellersToRooms(
      rooms,
      selectedTravellerEmployeeIds,
      selectedTravellerGuestIds,
    )

    const bundleItems = generateBundleAndSaveCheckoutItems(
      offer,
      packageView,
      dates,
      roomsWithTravellerInfo,
      currentCurrency,
    )
    dispatch(addToCheckoutCart(bundleItems))
    Analytics.trackEvent(addToCart())
    dispatch(navigateToCheckout())
  }, [bundledStates, currentCurrency, dispatch, offer, offerPageDispatch, packageView, rooms, selectedTravellerEmployeeIds, selectedTravellerGuestIds])
  const invalidRoomSelection = !rooms || isRoomsInvalid(rooms)

  const onBookNowClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    onSubmit?.()

    if (bundledStates.some(state => !state.checkInDate)) {
      e.preventDefault()

      offerPageDispatch({ type: OfferPageActions.setFilteredBundlePackageId, filteredBundlePackageId: packageView.bundlePackageId })

      // TODO redo for bundles action
      for (const option of packageView.options) {
        offerPageDispatch({ type: OfferPageActions.setRoomType, room: option.package.roomType, offerId: option.package.offerId })
      }

      offerPageDispatch({
        type: OfferPageActions.toggleSubFilter,
        filter: 'duration',
      })

      offerPageDispatch({
        type: OfferPageActions.toggleFilter,
        filter: 'checkin',
      })

      return
    }

    if (invalidRoomSelection) {
      e.preventDefault()

      // TODO redo for bundles action
      offerPageDispatch({
        type: OfferPageActions.toggleFilter,
        filter: 'capacity',
      })

      return
    }

    if (!isLoggedIn && !offer.walledGarden) {
      e.preventDefault()

      dispatch(accountAccessShowLogin())

      loginListener.awaitResult().then(({ loggedIn }) => {
        if (loggedIn) {
          onBookNow()
        }
      })
      return
    }

    onBookNow()
  }, [
    onSubmit,
    bundledStates,
    invalidRoomSelection,
    isLoggedIn,
    offer.walledGarden,
    onBookNow,
    offerPageDispatch,
    packageView.bundlePackageId,
    packageView.options,
    dispatch,
    loginListener,
  ])

  return (
    <TextButton {...rest} kind="primary" onClick={onBookNowClick} />
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    isLoggedIn: isLoggedInSelector(appState),
    bundledOffersState: state.bundledOffers,
    offer: state.offer,
    rooms: state.rooms,
  }
}, BundleAndSaveBookButton)
